import { formatDate, subMonths } from 'date-fns';
import type { TokenBalance } from '~/lib/.server/rateLimit';
import { formatTokens } from '~/utils/format';

const TOKENS_WARNING_THRESHOLD = 1_000_000;
const TOKENS_WARNING_PERCENT_THRESHOLD = 0.3;
const MAX_WIDTH_PERCENTAGE = 65;

export const needShowWarning = (maxAvailableTokens: number, usedTokens: number) => {
  const tokensLeft = maxAvailableTokens - usedTokens;
  return tokensLeft < Math.min(TOKENS_WARNING_PERCENT_THRESHOLD * maxAvailableTokens, TOKENS_WARNING_THRESHOLD);
};

export const calculateSafePercentage = (value: number, total: number): number => {
  return total > 0 ? (value / total) * 100 : 0;
};

type TokenData = {
  count: string;
  description: string;
  leftTokens: number;
  widthPercentage: number;
  monthlyCapacity?: number;
  widthMaxCapacity?: number;
  widthMonthlyToCapacity?: number;
};

export type RegularTokenBreakdown = TokenBalance & {
  expiresOn: number;
  type: 'rolled-over' | 'active-month';
};

interface GetTokenDataParams {
  tokens: RegularTokenBreakdown | number;
  maxTokens: number;
  isFreeTier?: boolean;
  isDowngradedToFree?: boolean;
}

export const getTokenData = (props: GetTokenDataParams): TokenData => {
  const { tokens, maxTokens, isFreeTier, isDowngradedToFree } = props;

  if (typeof tokens === 'number') {
    const leftTokens = tokens;
    const widthPercentage = calculateSafePercentage(leftTokens, maxTokens) * (MAX_WIDTH_PERCENTAGE / 100);

    return {
      count: `${formatTokens(tokens)} extra tokens`,
      description: 'Never expiring.',
      leftTokens,
      widthPercentage,
    };
  }

  /**
   * Sometimes users have negative token balances.
   */
  const leftTokens = Math.max(0, tokens.available - tokens.used);
  const monthlyCapacity = tokens.available;

  const widthPercentage = calculateSafePercentage(leftTokens, maxTokens) * (MAX_WIDTH_PERCENTAGE / 100);
  const widthMaxCapacity = calculateSafePercentage(monthlyCapacity, maxTokens) * (MAX_WIDTH_PERCENTAGE / 100);
  const widthMonthlyToCapacity = calculateSafePercentage(leftTokens, monthlyCapacity);

  if (!isFreeTier && tokens.type === 'rolled-over') {
    return {
      count: `${formatTokens(leftTokens)} rolled-over tokens`,
      description: `Expiring on ${formatDate(tokens.expiresOn, 'MMMM d, yyyy')}.`,
      leftTokens,
      widthPercentage,
    };
  }

  let description = '';

  if (isFreeTier || isDowngradedToFree) {
    const date = isDowngradedToFree ? subMonths(tokens.expiresOn, 1) : tokens.expiresOn;
    description = `Unused tokens expire ${formatDate(date, 'MMMM d, yyyy')}.`;
  } else {
    description = `Unused tokens will roll over and expire ${formatDate(tokens.expiresOn, 'MMMM d, yyyy')}.`;
  }

  return {
    count: `${formatTokens(leftTokens)}/${formatTokens(tokens.available)} monthly tokens`,
    description,
    leftTokens,
    monthlyCapacity,
    widthPercentage,
    widthMaxCapacity,
    widthMonthlyToCapacity,
  };
};
